import { fromNumber } from '../../domain/points';
import type { Exam, Person, SnippetNode, Station, Task } from '../../domain/types';
import { toRelayDate, toRelayDateTime } from '../../persistence/relay';
import type { ExamBundle } from '../../persistence/bundle';
import type {
  ActorRow, CandidateRow, ExamAnswerRow, ExamItemRow, ExamQuestionRow, ExaminerRow,
  PredefinedCommentPhraseRow,
} from '../../persistence/rows';
import { RELAY_SEED_NS, uuidv5 } from '../../platform/uuid';
import { SNIPPET_TREE } from './content';

/**
 * Wandelt die Beispieldaten in Relay-Form.
 *
 * Die Abbildung folgt `RELAY/memory/osce-terminology.md`:
 * Station = `ExamItemContainer` mit `type = 'STATION'`, Aufgabe = `ExamItem` mit
 * `type = 'TASK'` plus `ExamQuestion` im Verhältnis 1:1. `ExamPart` hat im OSCE keine
 * fachliche Entsprechung — die Prüfung trägt genau einen durchgeschleiften Teil, damit
 * `ExamItemContainer.examPartID` NOT NULL bleiben kann.
 *
 * Ab Phase 4 liefert der Server dieselbe Form; dann fällt diese Datei weg, `ExamBundle`
 * (`../../persistence/bundle`) bleibt.
 */

/** Stabile Kennung aus einem sprechenden Schlüssel — ein erneutes Einlesen trifft dieselbe Zeile. */
export function mockId(kind: string, key: string): string {
  return uuidv5(RELAY_SEED_NS, kind + '/' + key);
}

/** „Dr. M. Halter" → Vorname „Dr. M.", Nachname „Halter". */
function teileNamen(name: string): { firstName: string; lastName: string } {
  const teile = name.trim().split(/\s+/);
  return { firstName: teile.slice(0, -1).join(' ') || teile[0], lastName: teile[teile.length - 1] };
}

function personRows(person: Person) {
  return { id: mockId('Person', person.id), ...teileNamen(person.name) };
}

function aufgabe(examKey: string, station: Station, task: Task, index: number) {
  const itemId = mockId('ExamItem', examKey + '/' + station.id + '/' + task.id);
  const questionId = mockId('ExamQuestion', examKey + '/' + station.id + '/' + task.id);
  const max = fromNumber(
    task.scoring.kind === 'global'
      ? task.scoring.max
      : task.scoring.kind === 'checklist'
        ? task.scoring.items.reduce((sum, i) => sum + i.points, 0)
        : task.scoring.criteria.reduce((sum, c) => sum + Math.max(...c.options.map((o) => o.points)), 0),
  );

  const item: ExamItemRow = {
    id: itemId,
    examItemContainerID: mockId('ExamItemContainer', examKey + '/' + station.id),
    name: task.title,
    type: 'TASK',
    orderNo: index,
    maxPointsNumerator: max.n,
    maxPointsDenominator: max.d,
    // Der Kurztext des Bogens hat in Relay kein eigenes Feld; er steht als Ankertext dabei.
    anchorCriterion: task.short,
    comment: task.typeLabel,
  };

  const question: ExamQuestionRow = {
    id: questionId,
    examItemID: itemId,
    // Bei der globalen Bewertung ist der Text die Orientierungshilfe, deren Wortgruppen
    // markiert werden (`SELECTED_WORDS`).
    questionText: task.scoring.kind === 'global' ? task.scoring.body : null,
    vignette: null,
  };

  const answers: ExamAnswerRow[] = [];
  if (task.scoring.kind === 'checklist') {
    task.scoring.items.forEach((zeile, i) => {
      const punkte = fromNumber(zeile.points);
      answers.push({
        // Kennung hängt am Index, nicht am Text: bewusst so belassen. Inhaltsbasierte
        // Schlüssel brächen beim Umformulieren einer Zeile statt beim Einfügen — und eine
        // verschobene Antwortkennung trifft nur diese eine Zeile, nicht ganze Ketten wie bei
        // Station/Aufgabe.
        id: mockId('ExamAnswer', examKey + '/' + station.id + '/' + task.id + '/' + i),
        examQuestionID: questionId,
        answerText: zeile.text,
        orderNo: i,
        scalaValues: null,
        maxPointsNumerator: punkte.n,
        maxPointsDenominator: punkte.d,
        comment: null,
      });
    });
  } else if (task.scoring.kind === 'criteria') {
    task.scoring.criteria.forEach((kriterium, i) => {
      const punkte = fromNumber(Math.max(...kriterium.options.map((o) => o.points)));
      answers.push({
        // Kennung hängt am Index, nicht am Text: bewusst so belassen, aus demselben Grund
        // wie bei der Checkliste oben.
        id: mockId('ExamAnswer', examKey + '/' + station.id + '/' + task.id + '/' + i),
        examQuestionID: questionId,
        answerText: kriterium.label,
        orderNo: i,
        scalaValues: JSON.stringify(kriterium.options.map((o) => o.label)),
        maxPointsNumerator: punkte.n,
        maxPointsDenominator: punkte.d,
        comment: null,
      });
    });
  }

  return { item, question, answers };
}

/**
 * Die Bausteine der Beispieldaten, flach in der Form der Tabelle
 * `PredefinedCommentPhrase` — von der Wurzel nach unten, damit kein Kind vor seinem
 * Elternteil steht (der Fremdschlüssel zeigt auf dieselbe Tabelle).
 */
function bausteinZeilen(examKey: string): PredefinedCommentPhraseRow[] {
  const zeilen: PredefinedCommentPhraseRow[] = [];
  const listId = mockId('PhraseList', examKey);

  const gehe = (knoten: SnippetNode[], elternId: string | null, pfad: string) => {
    knoten.forEach((k, i) => {
      const schluessel = pfad + '/' + i;
      const id = mockId('Phrase', examKey + schluessel);
      zeilen.push({
        id,
        predefinedCommentPhraseListID: listId,
        parentPredefinedCommentPhraseID: elternId,
        name: k.label ?? k.text ?? '',
        fullText: k.children ? '' : (k.text ?? k.label ?? ''),
        orderNo: i,
      });
      if (k.children) gehe(k.children, id, schluessel);
    });
  };

  gehe(SNIPPET_TREE, null, '');
  return zeilen;
}

export function toBundle(exam: Exam): ExamBundle {
  const examId = mockId('Exam', exam.id);
  const partId = mockId('ExamPart', exam.id);
  const jetzt = new Date();

  const bundle: ExamBundle = {
    exam: {
      id: examId,
      name: exam.name,
      description: exam.meta,
      examType: 'OSCE',
      /*
       * ⛔ **Die Beispielpruefungen sind Simulationspruefungen** (Auftrag des Nutzers vom
       * 2026-09-20).
       *
       * ⚠️ **Bis dahin stand hier `'N'`, mit der Begruendung, die Demo liege nur auf dem Geraet
       * und sehe ohnehin keinen Server.** Das war falsch: `server/sync.ts` schickt jede offene
       * Protokollzeile, deren Pruefung nicht simulativ ist, und die Demo steht als ganz
       * normales `Exam` in der Ablage. Ist ein Server eingerichtet, gingen Uebungsbewertungen
       * in die echte Pruefungsdatenbank — und niemand saehe ihnen an, dass sie geuebt waren.
       *
       * `toBundle` bildet ausschliesslich Beispieldaten ab (`ladeBeispielpruefungen`); eine echte Pruefung kommt nie hier durch, sondern ueber
       * `server/dokument.ts`. Das Kennzeichen gehoert deshalb hierher und nicht an den
       * Einlesevorgang.
       */
      simulative: 'Y' as const,
      candidateInstructions: null,
      devicePassword: null,
      durationSeconds: null,
      startDate: toRelayDate(jetzt),
      startTime: '1000',
      endDate: null,
      endTime: '1200',
      lastModifiedDate: toRelayDateTime(jetzt),
      notice: null,
    },
    parts: [{ id: partId, examID: examId, name: exam.name, orderNo: 0 }],
    containers: [],
    items: [],
    questions: [],
    answers: [],
    examiners: [],
    examExaminers: [],
    actors: [],
    examActors: [],
    candidates: [],
    examCandidates: [],
    // ⚠️ **Auch die Bausteine gehören in die Beispieldaten.** Das Kommentarblatt liest sie seit
    // dem 2026-09-16 aus der Ablage (`persistence/bausteine.ts`) statt aus `SNIPPET_TREE`;
    // ohne sie stünde die Demo ohne Bausteinleiste da, obwohl der echte Server eine liefert.
    phraseLists: [{ id: mockId('PhraseList', exam.id), name: exam.name }],
    phrases: bausteinZeilen(exam.id),
    itemPhraseLists: [],
  };

  exam.stations.forEach((station, si) => {
    bundle.containers.push({
      id: mockId('ExamItemContainer', exam.id + '/' + station.id),
      examPartID: partId,
      name: station.label,
      type: 'STATION',
      durationSeconds: station.durationSeconds,
      orderNo: si,
      // Die Vignette hängt in Relay am Container. Die drei Absätze des Bogens werden als
      // JSON abgelegt, damit sie beim Zurücklesen wieder auseinandergehen.
      vignette: station.vignette ? JSON.stringify(station.vignette) : null,
      comment: station.meta,
    });

    station.tasks.forEach((task, ti) => {
      const { item, question, answers } = aufgabe(exam.id, station, task, ti);
      bundle.items.push(item);
      bundle.questions.push(question);
      bundle.answers.push(...answers);
      // Jede Aufgabe zeigt auf dieselbe Liste — so führt es auch der Classic-Server (P7).
      bundle.itemPhraseLists?.push({
        id: mockId('ExamItemPhraseList', exam.id + '/' + item.id),
        examItemID: item.id,
        predefinedCommentPhraseListID: mockId('PhraseList', exam.id),
      });
    });
  });

  for (const prüfer of exam.examiners) {
    const zeile: ExaminerRow = {
      ...personRows(prüfer),
      groupName: prüfer.meta,
      key: null,
      // Beispieldaten: keine echte Anmeldung. Der Server liefert später einen Hash.
      password: '',
    };
    bundle.examiners.push(zeile);
    bundle.examExaminers.push({
      id: mockId('ExamExaminer', exam.id + '/' + prüfer.id),
      examID: examId,
      examinerID: zeile.id,
    });
  }

  for (const schauspieler of exam.actors) {
    const zeile: ActorRow = { ...personRows(schauspieler), groupName: schauspieler.meta };
    bundle.actors.push(zeile);
    bundle.examActors.push({
      id: mockId('ExamActor', exam.id + '/' + schauspieler.id),
      examID: examId,
      actorID: zeile.id,
    });
  }

  for (const prüfling of exam.candidates) {
    const zeile: CandidateRow = {
      id: mockId('Candidate', prüfling.id),
      // Die Beispieldaten führen beide Namensteile selbst; `teileNamen` wäre hier ein Raten
      // an einer Stelle, die es nicht nötig hat.
      firstName: prüfling.firstName,
      lastName: prüfling.lastName,
      matriculationNumber: prüfling.matriculationNumber,
    };
    bundle.candidates.push(zeile);
    bundle.examCandidates.push({
      id: mockId('ExamCandidate', exam.id + '/' + prüfling.id),
      examID: examId,
      candidateID: zeile.id,
      examCandidateNo: prüfling.examNo,
    });
  }

  return bundle;
}

/**
 * Die Kennungen, unter denen die Beispielprüfungen auf dem Gerät liegen. Sie stehen auf keinem
 * Server; die Prüfungsauswahl darf sie deshalb nicht für verwaist halten (`mergeCatalogue`).
 */
export const beispielIds = (exams: readonly { id: string }[]): ReadonlySet<string> =>
  new Set(exams.map((e) => mockId('Exam', e.id)));
